import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type InspoImportEntrySource = 'in_app' | 'deep_link';

export interface OpenInspoImportOptions {
  prefilledUrl?: string;
  entry?: InspoImportEntrySource;
}

interface InspoImportContextValue {
  isOpen: boolean;
  prefilledUrl: string;
  entrySource: InspoImportEntrySource;
  openInspoImport: (options?: OpenInspoImportOptions) => void;
  closeInspoImport: () => void;
  /** Clears prefilled URL while modal stays open (e.g. user backs out of parse error from a deep link). */
  clearPrefilledUrl: () => void;
}

const InspoImportContext = createContext<InspoImportContextValue | null>(null);

export function useInspoImport(): InspoImportContextValue {
  const ctx = useContext(InspoImportContext);
  if (!ctx) {
    throw new Error('useInspoImport must be used within InspoImportProvider');
  }
  return ctx;
}

export function InspoImportProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [prefilledUrl, setPrefilledUrl] = useState('');
  const [entrySource, setEntrySource] = useState<InspoImportEntrySource>('in_app');

  const openInspoImport = useCallback((options: OpenInspoImportOptions = {}) => {
    const nextUrl = options.prefilledUrl?.trim() ?? '';
    setPrefilledUrl(nextUrl);
    setEntrySource(options.entry ?? 'in_app');
    setIsOpen(true);
  }, []);

  const closeInspoImport = useCallback(() => {
    setIsOpen(false);
    setPrefilledUrl('');
    setEntrySource('in_app');
  }, []);

  const clearPrefilledUrl = useCallback(() => {
    setPrefilledUrl('');
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      prefilledUrl,
      entrySource,
      openInspoImport,
      closeInspoImport,
      clearPrefilledUrl,
    }),
    [isOpen, prefilledUrl, entrySource, openInspoImport, closeInspoImport, clearPrefilledUrl],
  );

  return <InspoImportContext.Provider value={value}>{children}</InspoImportContext.Provider>;
}
